import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { formatEpisodeLabel, getItemStatus, toJsDate } from './dateUtils';

function getRecentlyAddedSortDate(item) {
  const addedAt = toJsDate(item.addedAt);
  if (addedAt) return addedAt;

  const airDate = toJsDate(item.airDate);
  if (airDate) return airDate;

  return toJsDate(item.releaseDate);
}

function sortRecentlyAdded(items) {
  return [...items].sort((a, b) => {
    const dateA = getRecentlyAddedSortDate(a);
    const dateB = getRecentlyAddedSortDate(b);
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    return dateB - dateA;
  });
}

function getDayKey(value) {
  const date = toJsDate(value);
  return date ? date.toISOString().slice(0, 10) : '';
}

function getEpisodeSeasonNumber(entry) {
  const match = String(entry.episodeLabel || '').match(/^S(\d+)E\d+$/i);
  return match ? Number(match[1]) : null;
}

export function compactRecentlyAddedItems(items) {
  const nonTv = [];
  const tvByShow = new Map();

  items.forEach((item) => {
    if (item.type !== 'tv' || item.isEpisodeEntry !== true) {
      nonTv.push(item);
      return;
    }

    const showId = item.showItem?.id || item.id.split('-')[0];
    if (!showId) {
      nonTv.push(item);
      return;
    }

    if (!tvByShow.has(showId)) tvByShow.set(showId, []);
    tvByShow.get(showId).push(item);
  });

  const compactedTv = [];

  tvByShow.forEach((entries, showId) => {
    const dayKeys = entries.map((entry) => getDayKey(entry.releaseDate)).filter(Boolean);
    const uniqueDayKeys = new Set(dayKeys);
    const allSameDay = uniqueDayKeys.size === 1 && entries.length > 0;

    const seasonsMap = new Map();
    entries.forEach((entry) => {
      const season = getEpisodeSeasonNumber(entry);
      if (!Number.isFinite(season)) return;
      if (!seasonsMap.has(season)) seasonsMap.set(season, []);
      seasonsMap.get(season).push(entry);
    });

    if (allSameDay) {
      const seasonNumbers = Array.from(seasonsMap.keys()).sort((a, b) => a - b);
      const subtitle = seasonNumbers.length === 1
        ? `Season ${seasonNumbers[0]}`
        : 'All Seasons';
      const representative = entries[0];
      compactedTv.push({
        ...representative,
        id: `${showId}-same-day`,
        episodeLabel: subtitle,
      });
      return;
    }

    const usedEntryIds = new Set();

    seasonsMap.forEach((seasonEntries, seasonNumber) => {
      const seasonDayKeys = seasonEntries
        .map((entry) => getDayKey(entry.releaseDate))
        .filter(Boolean);
      const isSingleSeasonDay = seasonDayKeys.length > 1 && new Set(seasonDayKeys).size === 1;

      if (isSingleSeasonDay) {
        const representative = seasonEntries[0];
        compactedTv.push({
          ...representative,
          id: `${showId}-season-${seasonNumber}-${seasonDayKeys[0]}`,
          episodeLabel: `Season ${seasonNumber}`,
        });
        seasonEntries.forEach((entry) => usedEntryIds.add(entry.id));
      }
    });

    entries.forEach((entry) => {
      if (!usedEntryIds.has(entry.id)) compactedTv.push(entry);
    });
  });

  return [...nonTv, ...compactedTv].sort((a, b) => {
    const dateA = getRecentlyAddedSortDate(a);
    const dateB = getRecentlyAddedSortDate(b);
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    return dateB - dateA;
  });
}

function getShowIdFromEpisodeRef(ref) {
  return ref?.parent?.parent?.parent?.parent?.id || null;
}

function shouldFallbackToFullMediaFetch(error) {
  return error?.code === 'failed-precondition' || error?.code === 'permission-denied';
}

/**
 * Fetches all episodes for a TV show from Firestore subcollections.
 * Reads: tv/{showId}/seasons/{seasonNumber}/episodes/{episodeNumber}
 *
 * @param {string} showId - Firestore document ID of the TV show in the `tv` collection
 * @returns {Promise<Array>} flat sorted array of episode objects
 */
export async function fetchEpisodesForShow(showId) {
  const seasonsSnap = await getDocs(collection(db, 'tv', showId, 'seasons'));
  const episodeArrays = await Promise.all(
    seasonsSnap.docs.map(async (seasonDoc) => {
      const epsSnap = await getDocs(
        collection(db, 'tv', showId, 'seasons', seasonDoc.id, 'episodes')
      );
      return epsSnap.docs.map((epDoc) => epDoc.data());
    })
  );
  return episodeArrays
    .flat()
    .sort((a, b) => {
      if (a.seasonNumber !== b.seasonNumber) return a.seasonNumber - b.seasonNumber;
      return a.episodeNumber - b.episodeNumber;
    });
}

/**
 * Fetches all media from the `movies` and `tv` collections, including
 * episodes from subcollections for TV shows.
 *
 * Returns a combined array sorted by title, with each TV show enriched
 * with an `episodes` array loaded from subcollections.
 *
 * @returns {Promise<Array>}
 */
export async function fetchAllMedia() {
  const [moviesSnap, tvSnap] = await Promise.all([
    getDocs(query(collection(db, 'movies'), orderBy('title', 'asc'))),
    getDocs(query(collection(db, 'tv'), orderBy('title', 'asc'))),
  ]);

  const movies = moviesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

  const tvShows = await Promise.all(
    tvSnap.docs.map(async (d) => {
      const show = { id: d.id, ...d.data() };
      const episodes = await fetchEpisodesForShow(d.id);
      return { ...show, episodes };
    })
  );

  return [...movies, ...tvShows].sort((a, b) => a.title.localeCompare(b.title));
}

/**
 * Fetches a bounded, pre-sorted recently added feed using Firestore queries.
 * Uses indexed reads from:
 * - movies (confirmed + releaseDate desc)
 * - tv (addedAt desc)
 * - collectionGroup episodes (confirmed + addedAt desc)
 *
 * If required indexes are missing, falls back to `fetchAllMedia` to keep the page working.
 */
export async function fetchRecentlyAddedMedia({ movieLimit = 80, tvLimit = 80, episodeLimit = 120 } = {}) {
  try {
    const [moviesSnap, tvSnap, confirmedEpisodesSnap] = await Promise.all([
      getDocs(
        query(
          collection(db, 'movies'),
          where('confirmed', '==', true),
          orderBy('releaseDate', 'desc'),
          limit(movieLimit)
        )
      ),
      getDocs(
        query(
          collection(db, 'tv'),
          orderBy('addedAt', 'desc'),
          limit(tvLimit)
        )
      ),
      getDocs(
        query(
          collectionGroup(db, 'episodes'),
          where('confirmed', '==', true),
          orderBy('addedAt', 'desc'),
          limit(episodeLimit)
        )
      ),
    ]);

    const movieItems = moviesSnap.docs
      .map((d) => ({ id: d.id, ...d.data(), type: 'movie' }))
      .filter((item) => getItemStatus(item) === 'catalogue');

    const tvItems = tvSnap.docs
      .map((d) => ({ id: d.id, ...d.data(), type: 'tv' }))
      .filter((item) => getItemStatus(item) === 'catalogue' || !!toJsDate(item.addedAt));

    const showIdsWithConfirmedEpisodes = new Set(
      confirmedEpisodesSnap.docs
        .map((d) => getShowIdFromEpisodeRef(d.ref))
        .filter(Boolean)
    );

    const showIds = Array.from(
      new Set(
        [...showIdsWithConfirmedEpisodes, ...tvItems.map((item) => item.id)]
      )
    );

    const showMap = new Map();
    await Promise.all(
      showIds.map(async (showId) => {
        const showSnap = await getDoc(doc(db, 'tv', showId));
        if (showSnap.exists()) {
          showMap.set(showId, { id: showSnap.id, ...showSnap.data(), type: 'tv' });
        }
      })
    );

    const episodeItems = confirmedEpisodesSnap.docs
      .map((d) => {
        const episode = d.data();
        const showId = getShowIdFromEpisodeRef(d.ref);
        const showItem = showMap.get(showId);
        if (!showItem) return null;

        return {
          ...showItem,
          id: `${showId}-${episode.seasonNumber}-${episode.episodeNumber}`,
          addedAt: episode.addedAt || null,
          airDate: episode.airDate,
          releaseDate: episode.airDate || showItem.releaseDate || null,
          episodeLabel: formatEpisodeLabel(episode),
          isEpisodeEntry: true,
          showItem,
        };
      })
      .filter(Boolean);

    const standaloneTvItems = tvItems.filter((item) => !showIdsWithConfirmedEpisodes.has(item.id));

    return sortRecentlyAdded([...movieItems, ...standaloneTvItems, ...episodeItems]);
  } catch (error) {
    if (!shouldFallbackToFullMediaFetch(error)) throw error;

    const all = await fetchAllMedia();
    const expanded = all.flatMap((item) => {
      if (item.type !== 'tv') {
        if (getItemStatus(item) === 'catalogue') return [item];
        return [];
      }

      const confirmedEpisodeEntries = (item.episodes || [])
        .filter((ep) => ep?.confirmed === true)
        .map((ep) => ({
          ...item,
          id: `${item.id}-${ep.seasonNumber}-${ep.episodeNumber}`,
          addedAt: ep.addedAt || null,
          airDate: ep.airDate,
          releaseDate: ep.airDate || item.releaseDate || null,
          episodeLabel: formatEpisodeLabel(ep),
          isEpisodeEntry: true,
          showItem: item,
        }));

      if (confirmedEpisodeEntries.length > 0) return confirmedEpisodeEntries;
      if (getItemStatus(item) === 'catalogue') return [item];
      return [];
    });

    return sortRecentlyAdded(expanded);
  }
}
